// ===========================================
// Prompt Modal - Create/Edit Dev Prompts
// ===========================================
// Modal for creating and editing AI IDE prompts
// Includes tag input, category selection, version changelog
// Now with Prompt Builder integration for intelligent prompt creation
// Includes quality scoring based on Task Brief framework

import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Save, 
  Tag, 
  Hash,
  AlertCircle,
  Sparkles,
  Wand2,
  Lock,
  Globe,
  Zap,  // Icon for Auto Generate button
  Brain, // Icon for AI-generated tags
  Target, // Quality indicator icon
  FileText, // Freeform mode icon
  ClipboardList // Task Brief mode icon
} from 'lucide-react';
import { 
  ROLE_CATEGORIES_ARRAY, 
  ACTION_CATEGORIES_ARRAY 
} from '../data/promptCategories';
import { promptExpertAPI } from '../services/api';  // API for auto-generate
import PromptExpertWorkbench from './PromptExpertWorkbench';
import TaskBriefBuilder from './TaskBriefBuilder';
import './PromptModal.scss';

const PromptModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  prompt,  // null for create, existing prompt for edit
  categories,
  agents 
}) => {
  // ===========================================
  // Form State
  // ===========================================
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState('fullStack');        // Primary: engineering role
  const [action, setAction] = useState('generate');     // Secondary: what action
  const [tags, setTags] = useState([]);                 // User's custom tags (manualTags)
  const [tagInput, setTagInput] = useState('');
  const [changelog, setChangelog] = useState('');
  
  // AI-generated tags (read-only, from semantic analysis)
  // These are displayed separately from user tags when editing
  const [aiTags, setAiTags] = useState([]);
  const [aiTagCategories, setAiTagCategories] = useState({ technologies: [], outcomes: [], patterns: [] });
  
  // Prompt Builder attribution - tracks if content was created via Prompt Builder
  const [createdByAI, setCreatedByAI] = useState(false);
  
  // Visibility - public prompts are visible to all users
  const [isPublic, setIsPublic] = useState(false);
  
  // UI State
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);  // Loading state for auto-generate
  
  // ===========================================
  // Quality Score State - Task Brief Framework
  // ===========================================
  // Real-time quality scoring based on the three-layer prompting framework
  const [qualityScore, setQualityScore] = useState(null);
  const [scoringContent, setScoringContent] = useState(false);
  
  // ===========================================
  // Prompt Type and Builder Mode State
  // ===========================================
  // Note: promptType tracks the type for Task Brief integration (setter used, value stored for future use)
  const [, setPromptType] = useState('freeform'); // freeform, task_brief, rule, context_block
  const [builderMode, setBuilderMode] = useState(false); // Toggle between freeform and Task Brief Builder
  
  // ===========================================
  // Prompt Builder State
  // ===========================================
  const [showWorkbench, setShowWorkbench] = useState(false);
  
  const titleInputRef = useRef(null);
  const tagInputRef = useRef(null);

  // ===========================================
  // Initialize form when prompt changes
  // ===========================================
  useEffect(() => {
    if (prompt) {
      // Editing existing prompt - preserve AI attribution and visibility
      setTitle(prompt.title || '');
      setContent(prompt.content || '');
      setDescription(prompt.description || '');
      // Use role field, fall back to category for backwards compatibility
      setRole(prompt.role || prompt.category || 'fullStack');
      setAction(prompt.action || 'generate');
      // User tags - use manualTags if available, fall back to tags for backwards compat
      setTags(prompt.manualTags || prompt.tags || []);
      setChangelog(''); // Reset changelog for edits
      setCreatedByAI(prompt.createdByAI || false); // Keep existing AI attribution
      setIsPublic(prompt.isPublic || false); // Keep existing visibility
      
      // AI-generated tags (read-only display)
      setAiTags(prompt.aiTags || []);
      setAiTagCategories(prompt.aiTagCategories || { technologies: [], outcomes: [], patterns: [] });
    } else {
      // Creating new prompt - reset all fields
      setTitle('');
      setContent('');
      setDescription('');
      setRole('fullStack'); // Default role - Full-Stack
      setAction('generate'); // Default action - most common
      setTags([]);
      setChangelog('');
      setCreatedByAI(false); // New prompts start without AI flag
      setIsPublic(false); // New prompts default to private
      
      // Reset AI tags for new prompts (will be generated on save)
      setAiTags([]);
      setAiTagCategories({ technologies: [], outcomes: [], patterns: [] });
      
      // Reset prompt type and builder mode for new prompts
      setPromptType('freeform');
      setBuilderMode(false);
    }
    setError(null);
    setShowWorkbench(false); // Close workbench when modal resets
    setBuilderMode(false); // Reset builder mode
  }, [prompt, isOpen]);

  // Focus title input on open
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ===========================================
  // Quality Scoring - Debounced Effect
  // ===========================================
  // Scores the prompt content against the Task Brief framework criteria
  // Debounced to avoid excessive API calls while typing
  useEffect(() => {
    // Skip if no content or content too short
    if (!content?.trim() || content.trim().length < 20) {
      setQualityScore(null);
      return;
    }
    
    // Debounce the scoring call
    const timer = setTimeout(async () => {
      setScoringContent(true);
      try {
        const response = await promptExpertAPI.scoreQuality({ 
          content: content.trim(),
          promptType: 'freeform' // Will support task_brief in Phase 5
        });
        if (response?.data?.success) {
          setQualityScore(response.data.data);
        }
      } catch (err) {
        // Silently fail - non-critical feature
        console.warn('Quality scoring failed:', err);
      }
      setScoringContent(false);
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(timer);
  }, [content]);

  // ===========================================
  // Task Brief Builder Handler
  // ===========================================
  // Apply completed Task Brief from the builder wizard
  const handleTaskBriefComplete = (data) => {
    setContent(data.content);
    setPromptType(data.promptType || 'task_brief');
    setBuilderMode(false); // Exit builder mode after completion
    setCreatedByAI(true); // Mark as AI-assisted
    
    // Auto-generate title if empty and we have a task
    if (!title.trim() && data.sections?.task) {
      // Extract first few words as title
      const taskWords = data.sections.task.split(' ').slice(0, 6).join(' ');
      setTitle(taskWords + (data.sections.task.split(' ').length > 6 ? '...' : ''));
    }
  };

  // ===========================================
  // Prompt Builder Handler
  // ===========================================
  // Apply generated content from the AI workbench to the form
  // Now accepts full data object: { content, category, description, tags }
  // Sets createdByAI flag to true when applying from workbench
  const handleApplyFromWorkbench = (data) => {
    // Handle both old format (string) and new format (object) for backwards compatibility
    if (typeof data === 'string') {
      setContent(data);
    } else {
      // New format: object with content, role, action, description, tags
      if (data.content) setContent(data.content);
      if (data.role) setRole(data.role);
      if (data.action) setAction(data.action);
      // Legacy: support category field
      if (data.category && !data.role) setRole(data.category);
      if (data.description) setDescription(data.description);
      if (data.tags?.length) setTags(data.tags);
    }
    // Mark as AI-created when applying content from the Prompt Builder
    setCreatedByAI(true);
    setShowWorkbench(false);
  };

  // ===========================================
  // Auto Generate Handler - LLM generates content from Title & Description
  // ===========================================
  // Quick one-click generation using the title and description as input
  // Calls the promptExpertAPI.generate endpoint to create prompt content
  const handleAutoGenerate = async () => {
    // Validate that we have at least a title to work with
    if (!title.trim()) {
      setError('Please enter a title first to auto-generate prompt content');
      return;
    }

    setError(null);
    setGenerating(true);

    try {
      // Build task description from title and description
      // If description exists, combine them for richer context
      const task = description.trim() 
        ? `${title.trim()}: ${description.trim()}`
        : title.trim();

      // Call the generate endpoint with task and current role/action settings
      const response = await promptExpertAPI.generate({
        task,
        role,
        action,
        outputFormat: 'A ready-to-use coding prompt that can be copied to AI IDEs like Cursor or Copilot'
      });

      if (response.data?.success && response.data?.data?.generated) {
        // Extract the prompt from the response
        // The AI returns markdown with the prompt often in a code block
        const generatedText = response.data.data.generated;
        
        // Try to extract just the prompt from code blocks if present
        const codeBlockMatch = generatedText.match(/```[\s\S]*?\n([\s\S]*?)```/);
        const extractedPrompt = codeBlockMatch 
          ? codeBlockMatch[1].trim() 
          : generatedText;

        setContent(extractedPrompt);
        setCreatedByAI(true);  // Mark as AI-created for attribution
      } else {
        setError('Failed to generate prompt content. Please try again.');
      }
    } catch (err) {
      console.error('Auto-generate error:', err);
      setError(err.response?.data?.message || 'Failed to generate prompt. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ===========================================
  // Handlers
  // ===========================================
  
  // Handle tag input
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      removeTag(tags.length - 1);
    }
  };

  const addTag = () => {
    const newTag = tagInput.trim().toLowerCase();
    if (newTag && !tags.includes(newTag) && tags.length < 10) {
      setTags([...tags, newTag]);
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!content.trim()) {
      setError('Prompt content is required');
      return;
    }
    
    setSaving(true);
    
    // Build prompt data
    const promptData = {
      title: title.trim(),
      content: content.trim(),
      description: description.trim(),
      role,         // Primary: engineering role (Full-Stack, Frontend, etc.)
      action,       // Secondary: what action (Generate, Debug, etc.)
      tags,
      createdByAI,  // Include AI attribution flag for new prompts
      isPublic      // Visibility: false = private, true = public
    };
    
    // Add changelog if editing and content changed
    if (prompt && content !== prompt.content && changelog.trim()) {
      promptData.changelog = changelog.trim();
    }
    
    const result = await onSave(promptData);
    
    setSaving(false);
    
    if (!result.success) {
      setError(result.error || 'Failed to save prompt');
    }
  };

  // Handle close
  const handleClose = () => {
    if (saving) return; // Don't close while saving
    onClose();
  };

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // ===========================================
  // Render
  // ===========================================
  if (!isOpen) return null;

  const isEditing = !!prompt;
  const contentChanged = isEditing && content !== prompt.content;

  return (
    <div className="prompt-modal__overlay" onClick={handleOverlayClick}>
      <div className="prompt-modal">
        {/* Header */}
        <div className="prompt-modal__header">
          <h2>
            <Sparkles size={18} />
            {isEditing ? 'Edit Prompt' : 'New Prompt'}
          </h2>
          <button 
            className="prompt-modal__close" 
            onClick={handleClose}
            disabled={saving}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="prompt-modal__form">
          {/* Error Message */}
          {error && (
            <div className="prompt-modal__error">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {/* Title */}
          <div className="prompt-modal__field">
            <label htmlFor="prompt-title">Title *</label>
            <input
              ref={titleInputRef}
              id="prompt-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., React Component Scaffold"
              maxLength={100}
              disabled={saving}
            />
          </div>

          {/* Description */}
          <div className="prompt-modal__field">
            <label htmlFor="prompt-description">Description</label>
            <input
              id="prompt-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief explanation of what this prompt does"
              maxLength={500}
              disabled={saving}
            />
          </div>

          {/* ================================================
              MODE TOGGLE - Freeform vs Task Brief Builder
          ================================================ */}
          <div className="prompt-modal__mode-toggle">
            <button
              type="button"
              className={`prompt-modal__mode-btn ${!builderMode ? 'active' : ''}`}
              onClick={() => setBuilderMode(false)}
              disabled={saving}
            >
              <FileText size={14} />
              Freeform
            </button>
            <button
              type="button"
              className={`prompt-modal__mode-btn ${builderMode ? 'active' : ''}`}
              onClick={() => setBuilderMode(true)}
              disabled={saving}
            >
              <ClipboardList size={14} />
              Task Brief Builder
            </button>
          </div>

          {/* ================================================
              TASK BRIEF BUILDER MODE
          ================================================ */}
          {builderMode ? (
            <TaskBriefBuilder
              onComplete={handleTaskBriefComplete}
              onCancel={() => setBuilderMode(false)}
            />
          ) : (
            <>
              {/* ================================================
                  PROMPT CONTENT with AI BUTTONS (Freeform Mode)
              ================================================ */}
              <div className="prompt-modal__field prompt-modal__field--content">
                <div className="prompt-modal__field-header">
                  <label htmlFor="prompt-content">Prompt Content *</label>
                  
                  {/* AI BUTTON GROUP - Auto Generate + Prompt Builder */}
                  <div className="prompt-modal__ai-btn-group">
                {/* AUTO GENERATE BUTTON - Quick LLM generation from Title/Description */}
                <button
                  type="button"
                  className="prompt-modal__auto-generate-btn"
                  onClick={handleAutoGenerate}
                  disabled={saving || generating || !title.trim()}
                  title={!title.trim() ? 'Enter a title first' : 'Auto-generate prompt from title & description'}
                >
                  {generating ? (
                    <>
                      <span className="prompt-modal__spinner" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap size={14} />
                      Auto Generate
                    </>
                  )}
                </button>

                {/* NEON YELLOW PROMPT BUILDER BUTTON */}
                <button
                  type="button"
                  className="prompt-modal__ai-btn"
                  onClick={() => setShowWorkbench(true)}
                  disabled={saving || generating}
                >
                  <Wand2 size={14} />
                  Prompt Builder
                </button>
              </div>
            </div>
            
            <textarea
              id="prompt-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your AI prompt here... This is what you'll copy to Cursor, Copilot, or other AI tools."
              maxLength={10000}
              disabled={saving}
              rows={8}
            />
            <div className="prompt-modal__content-meta">
              <div className="prompt-modal__char-count">
                {content.length} / 10,000
              </div>
              
              {/* ===========================================
                  QUALITY SCORE INDICATOR - Task Brief Framework
              =========================================== */}
              {qualityScore && (
                <div className={`prompt-modal__quality prompt-modal__quality--${qualityScore.grade.toLowerCase()}`}>
                  <Target size={12} />
                  <span className="prompt-modal__quality-grade">{qualityScore.grade}</span>
                  <span className="prompt-modal__quality-score">{qualityScore.score}%</span>
                  {qualityScore.suggestions?.length > 0 && (
                    <span className="prompt-modal__quality-tip">
                      Add: {qualityScore.suggestions.slice(0, 2).map(s => s.label || s).join(', ')}
                    </span>
                  )}
                </div>
              )}
              
              {/* Loading indicator for quality scoring */}
              {scoringContent && !qualityScore && (
                <div className="prompt-modal__quality prompt-modal__quality--loading">
                  <span className="prompt-modal__spinner" />
                  <span>Scoring...</span>
                </div>
              )}
            </div>
          </div>
            </>
          )}

            {/* Role & Action Row */}
            <div className="prompt-modal__row">
              {/* Role - Engineering perspective (primary) */}
              <div className="prompt-modal__field">
                <label htmlFor="prompt-role">Role</label>
                <select
                  id="prompt-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={saving}
                >
                  {ROLE_CATEGORIES_ARRAY.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Action - What you want to do (secondary) */}
              <div className="prompt-modal__field">
                <label htmlFor="prompt-action">Action</label>
                <select
                  id="prompt-action"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  disabled={saving}
                >
                  {ACTION_CATEGORIES_ARRAY.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

          {/* Visibility Toggle - Public/Private */}
          <div className="prompt-modal__field">
            <label>Visibility</label>
            <div className="prompt-modal__visibility-toggle">
              <button
                type="button"
                className={`prompt-modal__visibility-btn ${!isPublic ? 'active' : ''}`}
                onClick={() => setIsPublic(false)}
                disabled={saving}
              >
                <Lock size={14} />
                Private
              </button>
              <button
                type="button"
                className={`prompt-modal__visibility-btn ${isPublic ? 'active' : ''}`}
                onClick={() => setIsPublic(true)}
                disabled={saving}
              >
                <Globe size={14} />
                Public
              </button>
            </div>
            <span className="prompt-modal__visibility-hint">
              {isPublic 
                ? 'Public prompts are visible to all users' 
                : 'Private prompts are only visible to you'}
            </span>
          </div>

          {/* ===========================================
              USER TAGS - Custom tags from user's schema
          =========================================== */}
          <div className="prompt-modal__field">
            <label>
              <Tag size={14} />
              Tags
              <span className="prompt-modal__tag-hint">Press Enter or comma to add</span>
            </label>
            <div className="prompt-modal__tags-container">
              {tags.map((tag, index) => (
                <span key={tag} className="prompt-modal__tag prompt-modal__tag--user">
                  <Hash size={10} />
                  {tag}
                  <button 
                    type="button" 
                    onClick={() => removeTag(index)}
                    disabled={saving}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                ref={tagInputRef}
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value.replace(',', ''))}
                onKeyDown={handleTagInputKeyDown}
                onBlur={addTag}
                placeholder={tags.length === 0 ? "Add tags..." : ""}
                disabled={saving || tags.length >= 10}
                className="prompt-modal__tag-input"
              />
            </div>
            {tags.length >= 10 && (
              <span className="prompt-modal__tag-limit">Maximum 10 tags</span>
            )}
          </div>

          {/* ===========================================
              AI TAGS - Auto-generated semantic tags (read-only)
              Only shown when editing a prompt with existing AI tags
          =========================================== */}
          {isEditing && aiTags.length > 0 && (
            <div className="prompt-modal__field prompt-modal__field--ai-tags">
              <label className="prompt-modal__ai-tags-label">
                <Brain size={14} />
                AI Tags
                <span className="prompt-modal__tag-hint prompt-modal__tag-hint--ai">
                  Auto-generated from content analysis
                </span>
              </label>
              <div className="prompt-modal__tags-container prompt-modal__tags-container--ai">
                {/* Group tags by category for better visualization */}
                {aiTagCategories.outcomes?.length > 0 && (
                  <div className="prompt-modal__ai-tag-group">
                    <span className="prompt-modal__ai-tag-group-label">Outcomes:</span>
                    {aiTagCategories.outcomes.map((tag) => (
                      <span key={`outcome-${tag}`} className="prompt-modal__tag prompt-modal__tag--ai prompt-modal__tag--outcome">
                        <Sparkles size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {aiTagCategories.patterns?.length > 0 && (
                  <div className="prompt-modal__ai-tag-group">
                    <span className="prompt-modal__ai-tag-group-label">Patterns:</span>
                    {aiTagCategories.patterns.map((tag) => (
                      <span key={`pattern-${tag}`} className="prompt-modal__tag prompt-modal__tag--ai prompt-modal__tag--pattern">
                        <Sparkles size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {aiTagCategories.technologies?.length > 0 && (
                  <div className="prompt-modal__ai-tag-group">
                    <span className="prompt-modal__ai-tag-group-label">Tech:</span>
                    {aiTagCategories.technologies.map((tag) => (
                      <span key={`tech-${tag}`} className="prompt-modal__tag prompt-modal__tag--ai prompt-modal__tag--tech">
                        <Zap size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className="prompt-modal__ai-tags-note">
                AI tags are automatically generated when you save. They help with discovery and filtering.
              </span>
            </div>
          )}

          {/* Changelog (only show when editing and content changed) */}
          {isEditing && contentChanged && (
            <div className="prompt-modal__field prompt-modal__field--changelog">
              <label htmlFor="prompt-changelog">
                Version Changelog
                <span className="prompt-modal__version-badge">
                  v{prompt.currentVersion} → v{prompt.currentVersion + 1}
                </span>
              </label>
              <input
                id="prompt-changelog"
                type="text"
                value={changelog}
                onChange={(e) => setChangelog(e.target.value)}
                placeholder="What changed in this version?"
                maxLength={200}
                disabled={saving}
              />
            </div>
          )}

          {/* Footer */}
          <div className="prompt-modal__footer">
            <button 
              type="button" 
              className="btn-secondary"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>Saving...</>
              ) : (
                <>
                  <Save size={14} />
                  {isEditing ? 'Save Changes' : 'Create Prompt'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* ===========================================
          AI PROMPT WORKBENCH
      =========================================== */}
      <PromptExpertWorkbench
        isOpen={showWorkbench}
        onClose={() => setShowWorkbench(false)}
        onApply={handleApplyFromWorkbench}
        currentContent={content}
        currentRole={role}
        currentAction={action}
      />
    </div>
  );
};

export default PromptModal;
